import type { PrivacyNotice } from "./types";
import type { UiLanguage } from "./uiMessages";

export type TermsContent = {
  accountTermination: readonly string[];
  availableSupport: readonly string[];
  changesSuspension: readonly string[];
  changesTerms: readonly string[];
  governingLaw: readonly string[];
  limitationLiability: readonly string[];
  mandatoryRights: readonly string[];
  noWarranties: readonly string[];
  privacyAndContact: {
    beforePrivacy: string;
    betweenLinks: string;
    afterContacts: string;
  };
  savedMappingsExports: readonly string[];
  usingService: readonly string[];
};

export type PrivacyContent = {
  automatedDecisionMaking: string;
  childrenPolicy: string;
  complaintGuidance: string;
  controllerLink: { beforeContacts: string; afterContacts: string };
  cookies: readonly { description: string; name: string }[];
  dataNotCollected: readonly string[];
  dataSources: readonly string[];
  internationalTransfers: readonly string[];
  materialChanges: string;
  personalDataCollected: readonly string[];
  purposesAndLegalBases: readonly string[];
  recipients: readonly {
    description: string;
    name: string;
    privacyUrl: string;
  }[];
  requiredPersonalData: readonly string[];
  retention: readonly string[];
  rights: readonly string[];
  securityMeasures: readonly string[];
  serviceClosure: string;
  serviceClosureLink: { beforeTerms: string; afterTerms: string };
  usPrivacyDisclosure: string;
};

const ENGLISH_TERMS_CONTENT: TermsContent = {
  usingService: [
    "Nummem is a free and experimental service provided on a best-effort basis. By using the authenticated service, you agree to these Terms.",
  ],
  availableSupport: [
    "There is no service-level agreement, guaranteed availability, or guaranteed support. The service may be slow, interrupted, or unavailable.",
    "Usage and capacity limits apply. To protect service availability and shared resources, automated controls may temporarily limit or reject new registrations, new saved mappings, bulk exports, or resource-intensive requests when traffic, latency, errors, storage, memory, or database capacity reaches operational thresholds. Limits may change as the experimental service evolves. Account access, privacy requests, and deletion functions will remain available where technically practicable.",
  ],
  savedMappingsExports: [
    "Saved mappings are convenience storage, not archival or backup storage. Data may be lost, corrupted, or unavailable because of software, infrastructure, security, provider, or operational failures.",
    "Users should export anything important and keep their own copy.",
  ],
  changesSuspension: [
    "The operator may modify, suspend, or discontinue any part of the service. Reasonable advance notice and an opportunity to export saved data will be provided when practicable.",
    "Immediate suspension may occur for security, legal, abuse, provider, or emergency reasons.",
  ],
  accountTermination: [
    "You may delete your account at any time. The operator may suspend or terminate an account that threatens the service, violates law, abuses the service, or creates material operational risk.",
    "Ownership, warranty, liability, dispute, and other provisions that by their nature must continue will survive account or service termination.",
  ],
  noWarranties: [
    "To the maximum extent permitted by law, the service is provided as is and as available without promises that it will be uninterrupted, secure, error-free, accurate, or suitable for a particular purpose.",
  ],
  limitationLiability: [
    "To the maximum extent permitted by law, the operator is not responsible for indirect, incidental, special, or consequential losses arising from use of the service, inability to use it, loss or corruption of data, or service modification, suspension, or discontinuation.",
  ],
  mandatoryRights: [
    "These Terms do not exclude or limit rights or liability that applicable law does not allow to be excluded or limited. This includes mandatory consumer and privacy rights and, where applicable, liability for intentional conduct, gross negligence, death, or personal injury.",
  ],
  governingLaw: [
    "These Terms are governed by Serbian law, except where mandatory law gives you protections that cannot be changed by these Terms. Contact the operator first when practical so a dispute can be addressed informally. A dispute may be brought before a court or authority that has jurisdiction under applicable law.",
  ],
  changesTerms: [
    "The version and effective date change when these Terms materially change. Reasonable notice of material changes will be provided in the service or by email when practicable.",
  ],
  privacyAndContact: {
    beforePrivacy: "See the ",
    betweenLinks: " notice for personal-data handling and the ",
    afterContacts: " section for the operator and contact details.",
  },
};

const LOCALIZED_TERMS_CONTENT: Record<Exclude<UiLanguage, "en">, TermsContent> = {
  ru: {
    usingService: [
      "Nummem - бесплатный экспериментальный сервис, предоставляемый по мере доступности ресурсов. Используя сервис после входа в аккаунт, вы соглашаетесь с настоящими Условиями.",
    ],
    availableSupport: [
      "Соглашение об уровне обслуживания, гарантированная доступность и гарантированная поддержка отсутствуют. Сервис может работать медленно, с перерывами или быть недоступен.",
      "Действуют ограничения использования и пропускной способности. Для защиты доступности сервиса и общих ресурсов автоматические средства могут временно ограничить или отклонить новые регистрации, новые сохраненные сопоставления, массовый экспорт или ресурсоемкие запросы, когда трафик, задержки, ошибки, хранилище, память или емкость базы данных достигают рабочих порогов. Ограничения могут меняться по мере развития экспериментального сервиса. Доступ к аккаунту, запросы о конфиденциальности и функции удаления будут оставаться доступными, когда это технически возможно.",
    ],
    savedMappingsExports: [
      "Сохраненные сопоставления предназначены для удобства, а не для архивного или резервного хранения. Данные могут быть потеряны, повреждены или недоступны из-за сбоев программного обеспечения, инфраструктуры, безопасности, поставщика услуг или эксплуатации.",
      "Экспортируйте все важные данные и храните собственную копию.",
    ],
    changesSuspension: [
      "Оператор может изменить, приостановить или прекратить работу любой части сервиса. Когда это возможно, будет предоставлено разумное предварительное уведомление и возможность экспортировать сохраненные данные.",
      "Немедленная приостановка возможна по причинам безопасности, требованиям закона, из-за злоупотреблений, действий поставщика услуг или чрезвычайной ситуации.",
    ],
    accountTermination: [
      "Вы можете удалить аккаунт в любое время. Оператор может приостановить или прекратить действие аккаунта, который угрожает сервису, нарушает закон, злоупотребляет сервисом или создает существенный операционный риск.",
      "Положения о праве собственности, гарантиях, ответственности, спорах и другие положения, которые по своей природе должны продолжать действовать, сохраняют силу после прекращения действия аккаунта или сервиса.",
    ],
    noWarranties: [
      "В максимальной степени, разрешенной законом, сервис предоставляется как есть и по мере доступности без обещаний бесперебойной, безопасной, безошибочной и точной работы или пригодности для конкретной цели.",
    ],
    limitationLiability: [
      "В максимальной степени, разрешенной законом, оператор не отвечает за косвенные, случайные, специальные или последующие убытки, возникшие из-за использования сервиса, невозможности его использования, потери или повреждения данных, а также изменения, приостановки или прекращения работы сервиса.",
    ],
    mandatoryRights: [
      "Настоящие Условия не исключают и не ограничивают права или ответственность, которые применимое право не разрешает исключать или ограничивать. Это включает обязательные права потребителей и права на конфиденциальность, а также, когда применимо, ответственность за умышленные действия, грубую неосторожность, смерть или причинение вреда здоровью.",
    ],
    governingLaw: [
      "Настоящие Условия регулируются законодательством Сербии, кроме случаев, когда императивные нормы предоставляют вам гарантии, которые нельзя изменить настоящими Условиями. Когда это целесообразно, сначала свяжитесь с оператором, чтобы попытаться разрешить спор неформально. Спор может быть передан в суд или орган, обладающий юрисдикцией согласно применимому праву.",
    ],
    changesTerms: [
      "Версия и дата вступления в силу меняются при существенном изменении настоящих Условий. Когда это возможно, разумное уведомление о существенных изменениях предоставляется в сервисе или по электронной почте.",
    ],
    privacyAndContact: {
      beforePrivacy: "Сведения об обработке персональных данных приведены в разделе ",
      betweenLinks: ", а сведения об операторе и способах связи - в разделе ",
      afterContacts: ".",
    },
  },
  es: {
    usingService: [
      "Nummem es un servicio gratuito y experimental que se presta según los recursos disponibles. Al usar el servicio autenticado, aceptas estas Condiciones.",
    ],
    availableSupport: [
      "No existe un acuerdo de nivel de servicio ni se garantizan la disponibilidad o la asistencia. El servicio puede ser lento, interrumpirse o no estar disponible.",
      "Se aplican límites de uso y capacidad. Para proteger la disponibilidad del servicio y los recursos compartidos, los controles automatizados pueden limitar o rechazar temporalmente nuevos registros, nuevas correspondencias guardadas, exportaciones masivas o solicitudes que consuman muchos recursos cuando el tráfico, la latencia, los errores, el almacenamiento, la memoria o la capacidad de la base de datos alcancen los umbrales operativos. Los límites pueden cambiar a medida que evolucione el servicio experimental. El acceso a la cuenta, las solicitudes de privacidad y las funciones de eliminación seguirán disponibles cuando sea técnicamente posible.",
    ],
    savedMappingsExports: [
      "Las correspondencias guardadas se almacenan por comodidad, no como archivo ni copia de seguridad. Los datos pueden perderse, dañarse o dejar de estar disponibles por fallos del software, la infraestructura, la seguridad, el proveedor o las operaciones.",
      "Exporta todo lo importante y conserva tu propia copia.",
    ],
    changesSuspension: [
      "El responsable puede modificar, suspender o interrumpir cualquier parte del servicio. Cuando sea posible, se avisará con una antelación razonable y se ofrecerá la oportunidad de exportar los datos guardados.",
      "La suspensión inmediata puede producirse por motivos de seguridad, legales, de abuso, del proveedor o de emergencia.",
    ],
    accountTermination: [
      "Puedes eliminar tu cuenta en cualquier momento. El responsable puede suspender o cerrar una cuenta que amenace el servicio, infrinja la ley, abuse del servicio o genere un riesgo operativo importante.",
      "Las disposiciones sobre propiedad, garantías, responsabilidad, controversias y otras que por su naturaleza deban continuar seguirán vigentes tras el cierre de la cuenta o del servicio.",
    ],
    noWarranties: [
      "En la máxima medida permitida por la ley, el servicio se proporciona tal cual y según disponibilidad, sin promesas de que sea ininterrumpido, seguro, exacto, libre de errores o adecuado para un fin concreto.",
    ],
    limitationLiability: [
      "En la máxima medida permitida por la ley, el responsable no responde de pérdidas indirectas, incidentales, especiales o consecuentes derivadas del uso del servicio, de la imposibilidad de usarlo, de la pérdida o daño de datos, o de la modificación, suspensión o interrupción del servicio.",
    ],
    mandatoryRights: [
      "Estas Condiciones no excluyen ni limitan los derechos o la responsabilidad que la legislación aplicable no permita excluir o limitar. Esto incluye los derechos obligatorios de los consumidores y de privacidad y, cuando corresponda, la responsabilidad por conducta intencionada, negligencia grave, muerte o lesiones personales.",
    ],
    governingLaw: [
      "Estas Condiciones se rigen por la legislación de Serbia, salvo cuando una norma imperativa te otorgue protecciones que estas Condiciones no puedan modificar. Cuando resulte práctico, contacta primero con el responsable para intentar resolver la controversia de forma informal. La controversia puede presentarse ante un tribunal o una autoridad que tenga jurisdicción conforme a la legislación aplicable.",
    ],
    changesTerms: [
      "La versión y la fecha de entrada en vigor cambian cuando estas Condiciones se modifican de forma sustancial. Cuando sea posible, se avisará de los cambios sustanciales en el servicio o por correo electrónico con una antelación razonable.",
    ],
    privacyAndContact: {
      beforePrivacy: "Consulta el aviso de ",
      betweenLinks: " para conocer el tratamiento de datos personales y la sección ",
      afterContacts: " para consultar los datos del responsable y de contacto.",
    },
  },
};

export function getTermsContent(language: UiLanguage): TermsContent {
  return language === "en"
    ? ENGLISH_TERMS_CONTENT
    : LOCALIZED_TERMS_CONTENT[language];
}

type PrivacyTranslation = {
  automatedDecisionMaking: readonly [string, string];
  childrenPolicy: string;
  complaintGuidance: string;
  controllerLink: PrivacyContent["controllerLink"];
  cookieDescriptions: Record<string, string>;
  dataNotCollected: readonly string[];
  dataSources: readonly string[];
  internationalTransfers: readonly string[];
  materialChanges: string;
  personalDataCollected: readonly string[];
  purposesAndLegalBases: readonly string[];
  recipientDescriptions: Record<string, {
    activatedBy: string;
    dataShared: readonly string[];
    purpose: string;
  }>;
  requiredPersonalData: readonly string[];
  retention: readonly string[];
  rights: readonly string[];
  securityMeasures: readonly string[];
  serviceClosure: string;
  serviceClosureLink: PrivacyContent["serviceClosureLink"];
  usPrivacyDisclosure: string;
};

const LOCALIZED_PRIVACY_CONTENT: Record<
  Exclude<UiLanguage, "en">,
  PrivacyTranslation
> = {
  ru: {
    personalDataCollected: [
      "стабильный идентификатор аккаунта Google и подтвержденный адрес электронной почты",
      "идентификатор аккаунта Nummem, роль пользователя или администратора и состояние аккаунта",
      "время создания, изменения и последнего входа в аккаунт",
      "выбор получения писем о продукте и время предоставления или отзыва согласия",
      "версия уведомления о конфиденциальности, показанная при входе в аккаунт",
      "область авторизации, а также время создания и окончания сеанса",
      "IP-адрес и метаданные запроса, которые может регистрировать инфраструктура развертывания",
      "необратимые сетевые идентификаторы на основе HMAC и ограниченные суточные счетчики трафика или безопасности",
      "вводимые числа во время обработки каждого запроса кодирования",
      "сопоставления чисел и мнемонических слов, которые пользователь явно сохраняет",
      "приглашенные адреса электронной почты, добавленные в базу пользователей Nummem",
      "данные отправителя, текст сообщения, вложения, временные метки и сведения о маршрутизации, когда пользователь связывается с оператором",
    ],
    dataNotCollected: [
      "данные рекламы, аналитики, отслеживания или профилирования не собираются",
      "история чисел или мнемоник не хранится на сервере, если пользователь явно не сохраняет сопоставление",
      "пароль Google не собирается",
      "токен доступа Google, токен обновления и ID-токен не сохраняются",
      "сервис не запрашивает данные специальных категорий",
      "персональные данные не продаются; таргетированная реклама и межсайтовое отслеживание не используются",
    ],
    dataSources: [
      "пользователь, включая вводимые числа, сохраненные сопоставления и обращения",
      "Google, когда пользователь начинает и завершает вход через OpenID Connect",
      "администратор, который приглашает адрес электронной почты",
      "браузер, устройство и сеть, из которых поступает запрос",
      "инфраструктура Cloudflare и AWS, которая доставляет и защищает запросы",
      "записи Nummem, созданные при использовании сервиса, включая идентификаторы аккаунтов, сетевые ключи на основе HMAC, временные метки и агрегированные счетчики",
    ],
    requiredPersonalData: [
      "стабильный идентификатор Google и подтвержденный адрес электронной почты необходимы для доступа после входа; без них вход в аккаунт невозможен",
      "необходимые cookie сеанса и безопасности OAuth требуются для входа через Google; отказ оставляет публичное уведомление доступным, но отключает использование сервиса после входа",
      "числа требуются только для запрошенного кодирования; сохранение сопоставления и получение писем о продукте необязательны",
    ],
    purposesAndLegalBases: [
      "Предоставление аутентификации и кодирования чисел словами: необходимо для оказания запрошенного пользователем сервиса",
      "Хранение и получение явно сохраненных пользователем сопоставлений: необходимо для оказания запрошенного сервиса",
      "Временное восстановление выбранного пользователем общего сопоставления после входа: необходимо для запрошенного перехода в той же вкладке",
      "Предотвращение злоупотреблений и защита сервиса: законные интересы в защите аккаунтов, доступности и инфраструктуры с учетом документированной оценки баланса интересов",
      "Отправка необязательных новостей продукта или запросов обратной связи только после явного согласия: согласие",
      "Ответы на запросы о правах, обращения органов власти и инциденты: юридическая обязанность, когда она применима, иначе законные интересы",
    ],
    retention: [
      "Cookie сеанса: не более {0} секунд либо до выхода или удаления сеанса",
      "Cookie состояния Google OAuth: не более {0} секунд; удаляется после успешного обратного вызова",
      "Токены Google и несохраненные введенные числа: только в памяти во время запроса, намеренно не сохраняются",
      "Передача общего сопоставления через вход: действует {0} минут в sessionStorage той же вкладки, удаляется после успешного восстановления, а при чтении после истечения срока игнорируется и удаляется",
      "Сохраненные сопоставления чисел и мнемоник: до удаления пользователем, удаления аккаунта или окончания более длительного обязательного срока хранения",
      "Запись идентификации и авторизации аккаунта: пока приглашенный аккаунт активен, до удаления аккаунта или окончания обязательного срока хранения",
      "Настройка получения писем о продукте и временные метки: пока существует аккаунт, включая подтверждение отзыва согласия",
      "Суточные агрегированные данные аккаунта и HMAC-сети о трафике или безопасности: {0} дней",
      "Данные мониторинга Prometheus: не более {0} дней или {1} ГиБ в зависимости от того, какой предел приведет к более раннему удалению",
      "Журналы контейнеров приложения, базы данных и прокси: циклическое локальное хранилище не более трех файлов по {0} МиБ на контейнер; старые файлы удаляются автоматически",
      "Контактная электронная почта: хранится только столько, сколько необходимо для ответа, обработки запроса или инцидента и выполнения применимой юридической обязанности; почтовый ящик проверяется не реже одного раза в год",
      "Cloudflare, AWS, Google, Wikimedia и Microsoft могут хранить записи под своим управлением согласно применимым условиям и политикам конфиденциальности",
    ],
    cookieDescriptions: {
      nummem_session: "Сохраняет вход пользователя и разрешает запросы API. Содержит подписанный, но не зашифрованный случайный идентификатор аккаунта, область доступа, а также время создания и окончания сеанса; действует не более {0} секунд.",
      nummem_google_oauth_state: "Защищает вход через Google от подделки запроса и повторного использования токена. Содержит случайные значения состояния и nonce, а также время создания и окончания; действует не более {0} секунд.",
    },
    recipientDescriptions: {
      hosting: {
        purpose: "Размещает приложение, базу PostgreSQL, прокси, журналы и мониторинг",
        dataShared: [
          "IP-адрес",
          "время и маршрут запроса",
          "метаданные браузера и сети в зависимости от журналов хостинга",
          "трафик приложения, доступный там, где поставщик выполняет среду или завершает TLS",
        ],
        activatedBy: "каждый сетевой запрос к развернутому сервису",
      },
      edge: {
        purpose: "Предоставляет публичный DNS, TLS, доставку через прокси, межсетевой экран, ограничение частоты запросов и защиту от DDoS",
        dataShared: ["IP-адрес", "время запроса, имя хоста и маршрут", "метаданные браузера, устройства и сети", "события безопасности и результаты запросов"],
        activatedBy: "каждый публичный запрос к сервису",
      },
      wikimedia: {
        purpose: "Определяет языковую страницу Википедии через Wikidata и предоставляет управляемые пользователем ссылки и предпросмотры Википедии",
        dataShared: ["IP-адрес и метаданные браузера", "созданное мнемоническое слово, выбранная публичная метка каталога либо публичный идентификатор Wikidata или Википедии, запрошенный для определения или предпросмотра"],
        activatedBy: "предпросмотры включены по умолчанию; запрос выполняется, когда пользователь наводит указатель на элемент с предпросмотром или открывает ссылку Википедии",
      },
      email: {
        purpose: "Доставляет письма по вопросам конфиденциальности, разработки и поддержки",
        dataShared: ["адреса электронной почты отправителя и получателя", "текст сообщения и выбранные отправителем вложения", "время сообщения и метаданные маршрутизации почты"],
        activatedBy: "пользователь решает отправить письмо на опубликованный адрес",
      },
      google: {
        purpose: "Аутентифицирует выбранный пользователем аккаунт Google",
        dataShared: ["данные клиента OAuth и перенаправления", "случайные значения состояния и nonce"],
        activatedBy: "пользователь выбирает Продолжить с Google",
      },
    },
    internationalTransfers: [
      "Приложение и данные PostgreSQL размещены в {region}.",
      "Cloudflare, Google, Wikimedia, Microsoft и их субобработчики могут обрабатывать данные за пределами Сербии или ЕЭЗ. Когда применяется GDPR, оператор должен использовать решение об адекватности, Стандартные договорные положения или другую законную гарантию и по запросу предоставить сведения о ней.",
    ],
    securityMeasures: [
      "TLS защищает публичный трафик, а защищенные cookie HttpOnly - сеансы",
      "ограничения частоты запросов Cloudflare и приложения снижают риск злоупотреблений и отказа в обслуживании",
      "проверки роли и аккаунта ограничивают доступ к данным аккаунта",
      "порты базы данных и приложения закрыты за прокси",
      "ограниченные журналы и сетевые ключи на основе HMAC сокращают объем сохраняемых операционных данных",
    ],
    usPrivacyDisclosure: "Nummem не продает персональные данные, не передает их для поведенческой рекламы в разных контекстах, не использует таргетированную рекламу и не отслеживает действия на несвязанных сайтах. Поэтому сигналы Do Not Track и Global Privacy Control не вызывают дополнительных изменений обработки. Если эти практики изменятся, сервис сначала внедрит требуемую законом обработку сигналов и возможность отказа.",
    automatedDecisionMaking: [
      "Сервис не принимает юридически значимых или аналогичных решений в отношении пользователей.",
      "Сервис использует автоматизированное принятие решений, подпадающее под соответствующее регулирование.",
    ],
    rights: [
      "получить доступ к персональным данным и их копию",
      "исправить неточные персональные данные",
      "удалить данные, когда такое право применимо",
      "ограничить обработку, когда такое право применимо",
      "получить данные в переносимом формате, когда такое право применимо",
      "возразить против обработки на основании законных интересов",
      "в любое время отозвать необязательное согласие на письма о продукте во вкладке Профиль",
      "отправить запрос по адресу конфиденциальности; запросы об аккаунте могут потребовать входа или подтверждения через адрес электронной почты аккаунта",
      "получить ответ в течение одного месяца, когда применяется GDPR, с учетом законного продления; применимые сроки ответа и обжалования в США соблюдаются согласно законодательству соответствующего штата",
      "подать жалобу в компетентный надзорный орган ЕС или ЕЭЗ",
    ],
    complaintGuidance: "Когда это целесообразно, сначала свяжитесь с оператором. Вы также можете подать жалобу в надзорный орган по защите данных по месту постоянного проживания, работы или предполагаемого нарушения.",
    materialChanges: "Дата вступления в силу и дата последнего обновления меняются при существенном изменении настоящего уведомления. До начала новой или несовместимой цели обработки уведомление показывается в сервисе или отправляется по электронной почте, а когда новая обработка требует согласия, оно запрашивается.",
    childrenPolicy: "Nummem предназначен только для лиц в возрасте 18 лет и старше и не ориентирован на детей младше 13 лет. Доступ предоставляется только по приглашению. Если оператор узнает, что несовершеннолетний предоставил персональные данные, аккаунт будет приостановлен или удален в ходе необходимого реагирования.",
    serviceClosure: "При плановом окончательном закрытии сервиса записи аккаунтов и сохраненные сопоставления под управлением оператора удаляются после объявленного периода экспорта, кроме случаев, когда закон требует дальнейшего хранения. К журналам инфраструктуры, обращениям в поддержку и данным под управлением поставщиков применяются отдельные правила хранения, описанные выше.",
    serviceClosureLink: { beforeTerms: "См. раздел ", afterTerms: " с условиями доступности и прекращения работы." },
    controllerLink: { beforeContacts: "Сведения об операторе и способы связи приведены в разделе ", afterContacts: "." },
  },
  es: {
    personalDataCollected: [
      "identificador estable de la cuenta de Google y dirección de correo electrónico verificada",
      "identificador de cuenta de Nummem, función de usuario o administrador y estado de la cuenta",
      "fechas de creación, actualización y último acceso a la cuenta",
      "elección sobre los correos del producto y fecha de consentimiento o retirada",
      "versión del aviso de privacidad presentada al iniciar sesión",
      "ámbito de autenticación y fechas de emisión y vencimiento de la sesión",
      "dirección IP y metadatos de solicitud que puede registrar la infraestructura de despliegue",
      "identificadores de red irreversibles derivados mediante HMAC y contadores diarios limitados de tráfico o seguridad",
      "números introducidos mientras se procesa cada solicitud de codificación",
      "correspondencias entre números y palabras mnemotécnicas que el usuario guarda expresamente",
      "direcciones de correo invitadas incluidas en la base de datos de usuarios de Nummem",
      "datos del remitente, contenido del mensaje, archivos adjuntos, marcas de tiempo y metadatos de encaminamiento cuando el usuario contacta con el responsable",
    ],
    dataNotCollected: [
      "no se recopilan datos de publicidad, análisis, seguimiento ni elaboración de perfiles",
      "no se conserva en el servidor un historial de números o mnemotecnias salvo que el usuario guarde expresamente una correspondencia",
      "no se recopila la contraseña de Google",
      "no se conservan el token de acceso, el token de actualización ni el ID token de Google",
      "el servicio no solicita datos de categorías especiales",
      "no se venden datos personales ni se usan publicidad dirigida o seguimiento entre sitios",
    ],
    dataSources: [
      "el usuario, incluidos los números introducidos, las correspondencias guardadas y los mensajes de contacto",
      "Google, cuando el usuario inicia y completa el acceso mediante OpenID Connect",
      "el administrador que invita una dirección de correo electrónico",
      "el navegador, el dispositivo y la red que realizan la solicitud",
      "la infraestructura de Cloudflare y AWS que entrega y protege las solicitudes",
      "los registros generados al usar Nummem, incluidos los identificadores de cuenta, las claves de red derivadas mediante HMAC, las marcas de tiempo y los contadores agregados",
    ],
    requiredPersonalData: [
      "el identificador estable de Google y el correo verificado son necesarios para el acceso autenticado; sin ellos no se puede iniciar sesión",
      "las cookies necesarias de sesión y seguridad OAuth son obligatorias para acceder con Google; rechazarlas mantiene disponible el aviso público, pero desactiva el uso autenticado",
      "los números solo son necesarios para efectuar la codificación solicitada; guardar una correspondencia y recibir correos del producto son opcionales",
    ],
    purposesAndLegalBases: [
      "Proporcionar autenticación y codificación de números en palabras: necesario para prestar el servicio solicitado por el usuario",
      "Guardar y recuperar correspondencias que el usuario conserva expresamente: necesario para prestar el servicio solicitado",
      "Restaurar temporalmente una correspondencia compartida elegida por el usuario después del acceso: necesario para la navegación solicitada en la misma pestaña",
      "Prevenir abusos y proteger el servicio: intereses legítimos en proteger las cuentas, la disponibilidad y la infraestructura, sujetos a una prueba de ponderación documentada",
      "Enviar noticias opcionales del producto o solicitudes de opinión solo después de una aceptación expresa: consentimiento",
      "Responder a solicitudes de derechos, autoridades e incidentes: obligación legal cuando corresponda; en otro caso, intereses legítimos",
    ],
    retention: [
      "Cookie de sesión: como máximo {0} segundos o hasta cerrar sesión o eliminarla",
      "Cookie de estado de Google OAuth: como máximo {0} segundos; se elimina después de una devolución de llamada correcta",
      "Tokens de Google y números introducidos sin guardar: solo en la memoria de la solicitud, sin conservación intencionada",
      "Transferencia de una correspondencia compartida durante el acceso: válida durante {0} minutos en sessionStorage de la misma pestaña; se elimina después de restaurarla correctamente y se ignora y elimina si se lee tras el vencimiento",
      "Correspondencias guardadas de números y mnemotecnias: hasta que el usuario las elimine, borre la cuenta o termine una obligación legal de conservación más larga",
      "Registro de identidad y autorización de la cuenta: mientras la cuenta invitada siga activa, hasta eliminar la cuenta o terminar una obligación legal de conservación",
      "Preferencia y marcas de tiempo de los correos del producto: mientras exista la cuenta, incluida la prueba de retirada",
      "Agregados diarios de tráfico o seguridad por cuenta y red HMAC: {0} días",
      "Datos de supervisión de Prometheus: como máximo {0} días o {1} GiB, según el límite que los elimine antes",
      "Registros de los contenedores de la aplicación, la base de datos y el proxy: almacenamiento local rotativo limitado a tres archivos de {0} MiB por contenedor; los archivos antiguos se eliminan automáticamente",
      "Correo de contacto: se conserva solo durante el tiempo necesario para responder, gestionar una solicitud o incidente y cumplir una obligación legal aplicable; el buzón se revisa al menos una vez al año",
      "Cloudflare, AWS, Google, Wikimedia y Microsoft pueden conservar registros bajo su control conforme a sus condiciones y políticas de privacidad aplicables",
    ],
    cookieDescriptions: {
      nummem_session: "Mantiene la sesión del usuario y autoriza las solicitudes a la API. Contiene un identificador aleatorio de cuenta firmado pero no cifrado, el ámbito y las fechas de emisión y vencimiento; dura como máximo {0} segundos.",
      nummem_google_oauth_state: "Evita la falsificación de solicitudes de acceso con Google y la repetición de tokens. Contiene valores aleatorios de estado y nonce, y fechas de emisión y vencimiento; dura como máximo {0} segundos.",
    },
    recipientDescriptions: {
      hosting: {
        purpose: "Aloja la aplicación, la base de datos PostgreSQL, el proxy, los registros y la supervisión",
        dataShared: ["dirección IP", "hora y ruta de la solicitud", "metadatos del navegador y la red, según los registros del alojamiento", "tráfico de la aplicación disponible donde el proveedor ejecuta el entorno o termina TLS"],
        activatedBy: "cada solicitud de red al servicio desplegado",
      },
      edge: {
        purpose: "Proporciona DNS público, TLS, entrega mediante proxy, cortafuegos, limitación de frecuencia y protección frente a DDoS",
        dataShared: ["dirección IP", "hora de solicitud, nombre de host y ruta", "metadatos del navegador, el dispositivo y la red", "eventos de seguridad y resultados de solicitudes"],
        activatedBy: "cada solicitud pública al servicio",
      },
      wikimedia: {
        purpose: "Resuelve la identidad de Wikipedia específica del idioma mediante Wikidata y proporciona enlaces y vistas previas de Wikipedia controlados por el usuario",
        dataShared: ["dirección IP y metadatos del navegador", "palabra mnemotécnica generada, etiqueta pública seleccionada del catálogo o identificador público de Wikidata o Wikipedia solicitado para resolver la identidad o mostrar la vista previa"],
        activatedBy: "las vistas previas están activadas de forma predeterminada; se realiza una solicitud cuando el usuario pasa el puntero sobre un elemento con vista previa o abre un enlace de Wikipedia",
      },
      email: {
        purpose: "Entrega correos de privacidad, ingeniería y asistencia",
        dataShared: ["direcciones de correo del remitente y destinatario", "contenido del mensaje y archivos adjuntos elegidos por el remitente", "hora del mensaje y metadatos de encaminamiento del correo"],
        activatedBy: "el usuario decide enviar un correo a una dirección publicada",
      },
      google: {
        purpose: "Autentica la cuenta de Google seleccionada por el usuario",
        dataShared: ["datos del cliente OAuth y de redirección", "valores aleatorios de estado y nonce"],
        activatedBy: "el usuario selecciona Continuar con Google",
      },
    },
    internationalTransfers: [
      "La aplicación y los datos de PostgreSQL se alojan en {region}.",
      "Cloudflare, Google, Wikimedia, Microsoft y sus subencargados pueden tratar datos fuera de Serbia o del EEE. Cuando se aplique el RGPD, el responsable debe usar una decisión de adecuación, Cláusulas Contractuales Tipo u otra garantía legal y facilitar sus datos cuando se soliciten.",
    ],
    securityMeasures: [
      "TLS protege el tráfico público y las cookies seguras HttpOnly protegen las sesiones",
      "los límites de frecuencia de Cloudflare y de la aplicación reducen el abuso y el riesgo de denegación de servicio",
      "las comprobaciones de función y cuenta restringen el acceso a los datos de la cuenta",
      "los puertos de la base de datos y de la aplicación son privados detrás del proxy",
      "los registros limitados y las claves de red derivadas mediante HMAC reducen los datos operativos conservados",
    ],
    usPrivacyDisclosure: "Nummem no vende datos personales, no los comparte para publicidad conductual entre contextos, no usa publicidad dirigida ni sigue la actividad en sitios no relacionados. Por ello, las señales Do Not Track y Global Privacy Control no cambian el tratamiento. Si estas prácticas cambian, el servicio implantará primero la gestión de señales y la exclusión exigidas por la ley.",
    automatedDecisionMaking: [
      "El servicio no toma decisiones legales ni de importancia similar sobre los usuarios.",
      "El servicio usa decisiones automatizadas sujetas a la regulación aplicable.",
    ],
    rights: [
      "acceder a los datos personales y recibir una copia",
      "rectificar datos personales inexactos",
      "obtener la supresión cuando corresponda el derecho",
      "limitar el tratamiento cuando corresponda el derecho",
      "obtener la portabilidad de los datos cuando corresponda el derecho",
      "oponerse al tratamiento basado en intereses legítimos",
      "retirar en cualquier momento el consentimiento opcional para correos del producto en la pestaña Perfil",
      "enviar una solicitud al correo de privacidad; las solicitudes relativas a la cuenta pueden requerir iniciar sesión o verificar el correo de la cuenta",
      "recibir una respuesta en un mes cuando se aplique el RGPD, salvo prórroga legal; los plazos aplicables de respuesta y recurso en Estados Unidos se gestionan conforme a la ley estatal pertinente",
      "reclamar ante la autoridad de control competente de la UE o del EEE",
    ],
    complaintGuidance: "Cuando sea práctico, contacta primero con el responsable. También puedes reclamar ante la autoridad de protección de datos de tu residencia habitual, lugar de trabajo o del lugar de la supuesta infracción.",
    materialChanges: "La fecha de entrada en vigor y la de última actualización cambian cuando este aviso se modifica de forma sustancial. Antes de iniciar una finalidad de tratamiento nueva o incompatible, se muestra un aviso en el servicio o se envía por correo y, cuando el nuevo tratamiento lo requiere, se solicita el consentimiento.",
    childrenPolicy: "Nummem está destinado únicamente a personas de 18 años o más y no se dirige a menores de 13 años. El acceso continúa siendo solo por invitación. Si el responsable descubre que un menor ha facilitado datos personales, la cuenta se suspenderá o eliminará mientras se gestiona la respuesta necesaria.",
    serviceClosure: "En un cierre permanente planificado del servicio, los registros de cuentas y las correspondencias guardadas bajo el control del responsable se eliminan después del periodo de exportación anunciado, salvo cuando la ley exija conservarlos. Los registros de infraestructura, los datos de asistencia y los datos en poder de los proveedores siguen las reglas de conservación independientes descritas anteriormente.",
    serviceClosureLink: { beforeTerms: "Consulta las ", afterTerms: " para conocer las condiciones de disponibilidad e interrupción." },
    controllerLink: { beforeContacts: "Consulta la sección ", afterContacts: " para conocer la identidad y los datos de contacto del responsable." },
  },
};

function localizedList(
  source: readonly string[],
  translations: readonly string[],
): readonly string[] {
  return source.map((item, index) => translations[index] ?? item);
}

function injectNumbers(template: string, source: string): string {
  const values = source.match(/\d+(?:\.\d+)?/g) ?? [];
  return values.reduce(
    (text, value, index) => text.replaceAll(`{${index}}`, value),
    template,
  );
}

function hostingRegion(source: string): string {
  return source.match(/hosted in (.+)\.$/)?.[1] ?? source;
}

function recipientKind(
  name: string,
  index: number,
): "edge" | "email" | "google" | "hosting" | "wikimedia" | undefined {
  const normalized = name.toLowerCase();
  if (normalized.includes("cloudflare")) return "edge";
  if (normalized.includes("wikimedia")) return "wikimedia";
  if (normalized === "google") return "google";
  if (normalized.includes("outlook") || normalized.includes("mail")) return "email";
  return index === 0 ? "hosting" : undefined;
}

export function getPrivacyContent(
  notice: PrivacyNotice,
  language: UiLanguage,
): PrivacyContent {
  if (language === "en") return {
    automatedDecisionMaking: notice.automated_decision_making
      ? "The service uses covered automated decision-making."
      : "The service does not make legal or similarly significant decisions about users.",
    childrenPolicy: notice.children_policy,
    complaintGuidance: notice.complaint_guidance,
    controllerLink: {
      beforeContacts: "See the ",
      afterContacts: " section for the controller identity and contact details.",
    },
    cookies: notice.cookies.map((cookie) => ({
      name: cookie.name,
      description: `${cookie.purpose}. It contains ${cookie.personal_data} and lasts at most ${cookie.duration_seconds} seconds.`,
    })),
    dataNotCollected: notice.data_not_collected,
    dataSources: notice.data_sources,
    internationalTransfers: notice.international_transfers,
    materialChanges: notice.material_changes,
    personalDataCollected: notice.personal_data_collected,
    purposesAndLegalBases: notice.purposes_and_legal_bases,
    recipients: notice.third_parties.map((party) => ({
      name: party.name,
      description: `${party.purpose}. Data: ${party.data_shared.join(", ")}. Activated by: ${party.activated_by}.`,
      privacyUrl: party.privacy_url,
    })),
    requiredPersonalData: notice.required_personal_data,
    retention: notice.retention,
    rights: notice.rights,
    securityMeasures: notice.security_measures,
    serviceClosure: notice.service_closure,
    serviceClosureLink: {
      beforeTerms: "See the ",
      afterTerms: " for availability and discontinuation terms.",
    },
    usPrivacyDisclosure: notice.us_privacy_disclosure,
  };

  const translated = LOCALIZED_PRIVACY_CONTENT[language];
  const internationalTransfers = localizedList(
    notice.international_transfers,
    translated.internationalTransfers,
  ).map((item, index) => index === 0
    ? item.replace("{region}", hostingRegion(notice.international_transfers[0] ?? ""))
    : item);
  return {
    automatedDecisionMaking: translated.automatedDecisionMaking[
      notice.automated_decision_making ? 1 : 0
    ],
    childrenPolicy: translated.childrenPolicy,
    complaintGuidance: translated.complaintGuidance,
    controllerLink: translated.controllerLink,
    cookies: notice.cookies.map((cookie) => ({
      name: cookie.name,
      description: injectNumbers(
        translated.cookieDescriptions[cookie.name] ?? cookie.purpose,
        String(cookie.duration_seconds),
      ),
    })),
    dataNotCollected: localizedList(
      notice.data_not_collected,
      translated.dataNotCollected,
    ),
    dataSources: localizedList(notice.data_sources, translated.dataSources),
    internationalTransfers,
    materialChanges: translated.materialChanges,
    personalDataCollected: localizedList(
      notice.personal_data_collected,
      translated.personalDataCollected,
    ),
    purposesAndLegalBases: localizedList(
      notice.purposes_and_legal_bases,
      translated.purposesAndLegalBases,
    ),
    recipients: notice.third_parties.map((party, index) => {
      const kind = recipientKind(party.name, index);
      const content = kind
        ? translated.recipientDescriptions[kind]
        : undefined;
      const location = kind === "hosting"
        ? party.purpose.match(/monitoring in (.+)$/)?.[1]
        : undefined;
      const purpose = content
        ? `${content.purpose}${location ? ` ${language === "ru" ? "в" : "en"} ${location}` : ""}`
        : party.purpose;
      const dataShared = content
        ? localizedList(party.data_shared, content.dataShared)
        : party.data_shared;
      const activatedBy = content?.activatedBy ?? party.activated_by;
      return {
        name: party.name,
        description: language === "ru"
          ? `${purpose}. Данные: ${dataShared.join(", ")}. Условие активации: ${activatedBy}.`
          : `${purpose}. Datos: ${dataShared.join(", ")}. Se activa con: ${activatedBy}.`,
        privacyUrl: party.privacy_url,
      };
    }),
    requiredPersonalData: localizedList(
      notice.required_personal_data,
      translated.requiredPersonalData,
    ),
    retention: notice.retention.map((item, index) => injectNumbers(
      translated.retention[index] ?? item,
      item,
    )),
    rights: localizedList(notice.rights, translated.rights),
    securityMeasures: localizedList(
      notice.security_measures,
      translated.securityMeasures,
    ),
    serviceClosure: translated.serviceClosure,
    serviceClosureLink: translated.serviceClosureLink,
    usPrivacyDisclosure: translated.usPrivacyDisclosure,
  };
}
